import { useState } from 'react';
import { supabase } from '../utils/supabase';

const SECTIONS = [
  { id: 'general', label: 'General' },
  { id: 'concept', label: 'Concept & Brand' },
  { id: 'guest', label: 'Guest Experience' },
  { id: 'operations', label: 'Operations' },
  { id: 'menu', label: 'Menu & Beverage' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'roadmap', label: 'Roadmap' },
];

export default function ReportComments({ audit, onUpdate }) {
  const [newComment, setNewComment] = useState('');
  const [section, setSection] = useState('general');
  const [visibility, setVisibility] = useState('external');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('external');

  const comments = audit.comments || [];

  const addComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);

    const { data: { user } } = await supabase.auth.getUser();
    const authorName = user?.user_metadata?.full_name || user?.email || 'Unknown';

    const comment = {
      id: `comment_${Date.now()}`,
      section,
      visibility,
      text: newComment.trim(),
      author: authorName,
      authorId: user?.id || null,
      createdAt: new Date().toISOString(),
    };

    onUpdate({ ...audit, comments: [...comments, comment] });
    setNewComment('');
    setSubmitting(false);
  };

  const deleteComment = (commentId) => {
    onUpdate({ ...audit, comments: comments.filter(c => c.id !== commentId) });
  };

  const filteredComments = comments.filter(c => (c.visibility || 'external') === activeTab);

  const grouped = {};
  SECTIONS.forEach(s => { grouped[s.id] = filteredComments.filter(c => c.section === s.id); });

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-1">Notes & Commentary</h3>
      <p className="text-xs text-neutral-500 mb-4">Internal notes stay private. External notes appear in client reports.</p>

      {/* Tab toggle */}
      <div className="flex gap-1 mb-4 bg-neutral-800/40 rounded-lg p-0.5 w-fit">
        <button
          onClick={() => setActiveTab('external')}
          className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${activeTab === 'external' ? 'bg-brand-gold/15 text-brand-gold' : 'text-neutral-500 hover:text-neutral-300'}`}
        >
          External (Client-Facing)
        </button>
        <button
          onClick={() => setActiveTab('internal')}
          className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${activeTab === 'internal' ? 'bg-neutral-700 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}
        >
          Internal (Team Only)
        </button>
      </div>

      {/* Add comment */}
      <div className="bg-neutral-800/40 rounded-lg p-4 mb-5">
        <div className="flex gap-2 mb-2">
          <select value={section} onChange={(e) => setSection(e.target.value)} className="input-field text-sm py-1.5 w-40">
            {SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="input-field text-sm py-1.5 w-36">
            <option value="external">External</option>
            <option value="internal">Internal</option>
          </select>
        </div>
        <div className="flex gap-2">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={visibility === 'internal' ? 'Internal team note (clients will NOT see this)...' : 'Client-facing note (will appear in reports)...'}
            rows={2}
            className="input-field text-sm flex-1"
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), addComment())}
          />
          <button onClick={addComment} disabled={!newComment.trim() || submitting} className="btn-primary text-xs self-end py-2 disabled:opacity-40">Add</button>
        </div>
      </div>

      {/* Comments by section */}
      {SECTIONS.map(s => {
        const sectionComments = grouped[s.id] || [];
        if (sectionComments.length === 0) return null;
        return (
          <div key={s.id} className="mb-4">
            <p className="text-xs text-brand-gold font-semibold mb-2 uppercase tracking-wider">{s.label}</p>
            <div className="space-y-2">
              {sectionComments.map(c => (
                <div key={c.id} className={`rounded-lg px-4 py-3 ${c.visibility === 'internal' ? 'bg-neutral-800/50 border border-neutral-700/50' : 'bg-neutral-800/30 border border-brand-gold/10'}`}>
                  <p className="text-sm text-neutral-300">{c.text}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-neutral-600">
                      {c.author} — {new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {c.visibility === 'internal' && <span className="ml-1 text-neutral-600">(internal)</span>}
                    </p>
                    <button onClick={() => deleteComment(c.id)} className="text-[10px] text-neutral-600 hover:text-red-400 transition-colors">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {filteredComments.length === 0 && (
        <p className="text-neutral-600 text-sm text-center py-2">No {activeTab} notes yet.</p>
      )}
    </div>
  );
}

// Export for use in report/client views
export function getExternalComments(audit) {
  return (audit.comments || []).filter(c => (c.visibility || 'external') === 'external');
}

export { SECTIONS };
